import { useMemo } from "react";
import prettyBytes from "pretty-bytes";

import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import { Download, File } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Counter from "ds/components/Counter";
import Icon from "ds/components/Icon";
import TableSimple from "ds/components/TableSimple";
import Typography from "ds/components/Typography";
import { TerraformProviderVersionArtifact } from "types/generated";

import styles from "./styles.module.css";
import { PROVIDER_VERSION_ARTIFACTS_TABLE_CELLS } from "./Table/Cells";
import { SORT_CONFIG } from "./Table/constants";
import useProviderVersionArtifactsTableStore from "./Table/useTableStore";
import { ProviderVersionArtifactsProps } from "./type";
import useDownloadArtifact from "./useDownloadArtifact";

type ArtifactWithContext = TerraformProviderVersionArtifact & {
  providerId: string;
  versionNumber: string;
};

function ProviderVersionArtifacts({ version, provider, simple }: ProviderVersionArtifactsProps) {
  const { handleDownloadArtifact, loading } = useDownloadArtifact({
    providerId: provider.id,
    versionNumber: version.number,
  });

  const artifactsWithContext = useMemo<ArtifactWithContext[]>(
    () =>
      version.artifacts.map((artifact) => ({
        ...artifact,
        providerId: provider.id,
        versionNumber: version.number,
      })),
    [version.artifacts, version.number, provider.id]
  );

  if (simple) {
    return (
      <TableSimple
        data={artifactsWithContext}
        store={useProviderVersionArtifactsTableStore}
        ariaLabel="Provider version artifacts"
        cellPerColumn={PROVIDER_VERSION_ARTIFACTS_TABLE_CELLS}
        sortConfig={SORT_CONFIG}
        itemIdKey="filename"
      />
    );
  }

  return (
    <Box className={styles.tableWrapper} direction="column" fullWidth>
      <Box className={styles.tableHeader} align="center" gap="medium">
        <Typography tag="span" variant="p-t6">
          Assets
        </Typography>
        <Counter count={version.artifacts.length} />
      </Box>
      <FiltersSortHeaderWrapper
        columnOrder="minmax(40rem, 7fr) minmax(5rem, 1fr) 2.4rem"
        columnGap="large"
        ariaRole="row"
      >
        <FiltersSortHeaderStaticColumn role="columnheader">
          <Typography tag="span" variant="p-t7">
            Name
          </Typography>
        </FiltersSortHeaderStaticColumn>
        <FiltersSortHeaderStaticColumn role="columnheader">
          <Typography tag="span" variant="p-t7">
            Size
          </Typography>
        </FiltersSortHeaderStaticColumn>
      </FiltersSortHeaderWrapper>
      <Box direction="column">
        {version.artifacts.map((artifact) => (
          <Box
            className={styles.tableRow}
            key={artifact.filename}
            grid
            gap="large"
            gridTemplate="minmax(40rem, 7fr) minmax(5rem, 1fr) 2rem"
            role="row"
          >
            <Box align="center" gap="medium" role="rowheader">
              <Icon src={File} />
              <Typography tag="span" variant="p-body2">
                {artifact.filename}
              </Typography>
            </Box>
            <Box align="center" role="cell">
              <Typography tag="span" variant="p-body2">
                {prettyBytes(artifact.sizeBytes)}
              </Typography>
            </Box>
            <Box align="center" role="cell">
              <ButtonIcon
                onPress={() => handleDownloadArtifact(artifact)}
                variant="secondary"
                size="small"
                disabled={loading}
                icon={Download}
                tooltipPlacement="left"
              >
                Download artifact
              </ButtonIcon>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ProviderVersionArtifacts;
